/**
 * ShiftDialog Component
 * Dialog for adding/editing employee shifts
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';

import { DAYS_OF_WEEK } from '../constants';
import { formatTimeInput } from '../utils/timeHelpers';
import { getWeekDateRange } from '../utils/dateHelpers';

/**
 * ShiftDialog - Modal for adding/editing shifts
 * 
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Close handler
 * @param {boolean} props.isMobile - Mobile view flag
 * @param {Object} props.selectedCell - Selected cell data {employee, day, existingShift}
 * @param {Object} props.quickAddForm - Form state {selectedDays, weekOffset, dayTimes}
 * @param {Function} props.setQuickAddForm - Form state setter
 * @param {Object} props.selectedWeek - Selected week (dayjs object)
 * @param {Array} props.presetHours - Preset hour options
 * @param {Array} props.shifts - All shifts data
 * @param {Function} props.onDayToggle - Day toggle handler
 * @param {Function} props.onSave - Save handler
 */
export default function ShiftDialog({
  open,
  onClose,
  isMobile,
  selectedCell,
  quickAddForm,
  setQuickAddForm,
  selectedWeek,
  presetHours,
  shifts,
  onDayToggle,
  onSave,
}) {
  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: isMobile ? {
          m: 0,
          maxHeight: '100%',
          borderRadius: 0,
        } : {}
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        pb: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box>
          <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
            {selectedCell?.existingShift ? 'Επεξεργασία' : 'Προσθήκη'} Βάρδιας
          </Typography>
          {selectedCell && (
            <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
              {selectedCell.employee.name}
            </Typography>
          )}
        </Box>
        {isMobile && (
          <IconButton onClick={onClose} edge="end">
            <DeleteIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 3 } }}>
          
          {/* Week Selection - Compact */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 1.5, 
              bgcolor: 'grey.50', 
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1
            }}
          >
            <IconButton 
              onClick={() => setQuickAddForm({ ...quickAddForm, weekOffset: quickAddForm.weekOffset - 1 })}
              size="small"
              sx={{ bgcolor: 'white', boxShadow: 1 }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 600, 
                textAlign: 'center',
                minWidth: { xs: 180, sm: 200 }
              }}
            >
              📅 {getWeekDateRange(selectedWeek, quickAddForm.weekOffset)}
            </Typography>
            <IconButton 
              onClick={() => setQuickAddForm({ ...quickAddForm, weekOffset: quickAddForm.weekOffset + 1 })}
              size="small"
              sx={{ bgcolor: 'white', boxShadow: 1 }}
            >
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </Paper>

          {/* Bulk Apply - Sticky on mobile */}
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              bgcolor: 'primary.50', 
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'primary.200',
              position: isMobile ? 'sticky' : 'static',
              top: 0,
              zIndex: 10
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'primary.main' }}>
              ⚡ Γρήγορη εφαρμογή σε όλες τις μέρες:
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <TextField
                size="small"
                placeholder="09:00"
                inputProps={{ 
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  style: { 
                    fontSize: '0.9rem', 
                    textAlign: 'center',
                    fontWeight: 500,
                    letterSpacing: '1px'
                  }
                }}
                onChange={(e) => {
                  e.target.value = formatTimeInput(e.target.value);
                }}
                sx={{ 
                  width: { xs: '80px', sm: '90px' },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: '#f5f5f5' },
                    '&.Mui-focused': { 
                      bgcolor: '#fff',
                      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
                    }
                  },
                  '& input': { 
                    p: '10px 8px'
                  }
                }}
                id="bulk-start-time"
              />
              <Typography variant="body1" sx={{ fontWeight: 300, color: 'text.secondary' }}>→</Typography>
              <TextField
                size="small"
                placeholder="17:00"
                inputProps={{ 
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  style: { 
                    fontSize: '0.9rem', 
                    textAlign: 'center',
                    fontWeight: 500,
                    letterSpacing: '1px'
                  }
                }}
                onChange={(e) => {
                  e.target.value = formatTimeInput(e.target.value);
                }}
                sx={{ 
                  width: { xs: '80px', sm: '90px' },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    bgcolor: 'background.paper',
                    '&:hover': { bgcolor: '#f5f5f5' },
                    '&.Mui-focused': { 
                      bgcolor: '#fff',
                      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
                    }
                  },
                  '& input': { 
                    p: '10px 8px'
                  }
                }}
                id="bulk-end-time"
              />
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  const bulkStart = document.getElementById('bulk-start-time').value;
                  const bulkEnd = document.getElementById('bulk-end-time').value;
                  if (bulkStart && bulkEnd) {
                    const newDayTimes = { ...quickAddForm.dayTimes };
                    DAYS_OF_WEEK.forEach(day => {
                      newDayTimes[day.value] = { startTime: bulkStart, endTime: bulkEnd };
                    });
                    setQuickAddForm({ ...quickAddForm, dayTimes: newDayTimes });
                  }
                }}
                sx={{ 
                  ml: 'auto',
                  minWidth: { xs: 'auto', sm: 100 },
                  fontWeight: 600
                }}
              >
                Εφαρμογή
              </Button>
            </Box>
          </Paper>

          {/* Days Selection - Balanced */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, textAlign: 'center' }}>
              Επιλέξτε Ημέρες & Ωράριο:
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {DAYS_OF_WEEK.map((day, index) => {
                const isSelected = quickAddForm.selectedDays.includes(day.value);
                const existingShiftForDay = selectedCell?.employee 
                  ? shifts.find(s => s.employee_id === selectedCell.employee.id && String(s.day) === String(day.value))
                  : null;
                const hasExistingShift = !!existingShiftForDay;
                
                return (
                  <Box
                    key={day.value}
                    sx={{ 
                      py: 1.5,
                      px: 2,
                      borderBottom: index < DAYS_OF_WEEK.length - 1 ? '1px solid' : 'none',
                      borderColor: 'grey.200',
                      bgcolor: isSelected ? 'primary.50' : hasExistingShift ? 'success.50' : index % 2 === 0 ? 'grey.50' : 'transparent',
                      borderLeft: '4px solid',
                      borderLeftColor: isSelected ? 'primary.main' : hasExistingShift ? 'success.main' : 'transparent',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {/* Row 1: Checkbox + Day + Inputs + Fixed Badge Column */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5 }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => onDayToggle(day.value)}
                        size="small"
                        sx={{ p: 0.5 }}
                      />
                      <Typography 
                        variant="body2" 
                        onClick={() => onDayToggle(day.value)}
                        sx={{ 
                          fontWeight: 600,
                          minWidth: 85,
                          cursor: 'pointer',
                          color: isSelected ? 'primary.main' : hasExistingShift ? 'success.main' : 'text.primary'
                        }}
                      >
                        {day.label}
                      </Typography>
                      
                      {/* Compact Time Inputs */}
                      <TextField
                        size="small"
                        value={quickAddForm.dayTimes[day.value]?.startTime || ''}
                        onChange={(e) => {
                          const formatted = formatTimeInput(e.target.value);
                          const newDayTimes = { ...quickAddForm.dayTimes };
                          newDayTimes[day.value] = { ...newDayTimes[day.value], startTime: formatted };
                          setQuickAddForm({ ...quickAddForm, dayTimes: newDayTimes });
                        }}
                        placeholder="09:00"
                        inputProps={{ 
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          style: { 
                            fontSize: '0.9rem', 
                            textAlign: 'center',
                            fontWeight: 500,
                            letterSpacing: '1px'
                          }
                        }}
                        sx={{ 
                          width: { xs: 72, sm: 80 },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            bgcolor: 'background.paper',
                            height: 38,
                            '&:hover': { bgcolor: '#f5f5f5' },
                            '&.Mui-focused': { 
                              bgcolor: '#fff',
                              boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
                            }
                          },
                          '& input': { 
                            p: '8px 4px'
                          }
                        }}
                      />
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 300 }}>→</Typography>
                      <TextField
                        size="small"
                        value={quickAddForm.dayTimes[day.value]?.endTime || ''}
                        onChange={(e) => {
                          const formatted = formatTimeInput(e.target.value);
                          const newDayTimes = { ...quickAddForm.dayTimes };
                          newDayTimes[day.value] = { ...newDayTimes[day.value], endTime: formatted };
                          setQuickAddForm({ ...quickAddForm, dayTimes: newDayTimes });
                        }}
                        placeholder="17:00"
                        inputProps={{ 
                          inputMode: 'numeric',
                          pattern: '[0-9]*',
                          style: { 
                            fontSize: '0.9rem', 
                            textAlign: 'center',
                            fontWeight: 500,
                            letterSpacing: '1px'
                          }
                        }}
                        sx={{ 
                          width: { xs: 72, sm: 80 },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            bgcolor: 'background.paper',
                            height: 38,
                            '&:hover': { bgcolor: '#f5f5f5' },
                            '&.Mui-focused': { 
                              bgcolor: '#fff',
                              boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)'
                            }
                          },
                          '& input': { 
                            p: '8px 4px'
                          }
                        }}
                      />
                    </Box>
                    
                    {/* Row 2: Preset Chips (centered) */}
                    {presetHours && presetHours.length > 0 && (
                      <Box sx={{ display: 'flex', gap: 0.75, mt: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                        {presetHours.map((preset, idx) => {
                          const [startTime, endTime] = preset.split('-');
                          const isCurrentPreset = 
                            quickAddForm.dayTimes[day.value]?.startTime === startTime && 
                            quickAddForm.dayTimes[day.value]?.endTime === endTime;
                          return (
                            <Chip
                              key={idx}
                              label={preset}
                              size="small"
                              color={isCurrentPreset ? "primary" : "default"}
                              variant="outlined"
                              onClick={() => {
                                const newDayTimes = { ...quickAddForm.dayTimes };
                                newDayTimes[day.value] = { startTime, endTime };
                                setQuickAddForm({ ...quickAddForm, dayTimes: newDayTimes });
                              }}
                              sx={{ 
                                cursor: 'pointer', 
                                fontWeight: 500,
                                fontSize: '0.75rem',
                                height: 28,
                                borderRadius: '14px',
                                transition: 'all 0.2s ease',
                                borderColor: isCurrentPreset ? 'primary.main' : 'grey.300',
                                bgcolor: isCurrentPreset ? 'primary.50' : 'transparent',
                                '&:hover': { 
                                  bgcolor: isCurrentPreset ? 'primary.100' : 'grey.100',
                                  borderColor: isCurrentPreset ? 'primary.main' : 'grey.400',
                                  transform: 'scale(1.02)'
                                }
                              }}
                            />
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* Actions - Fixed at bottom on mobile */}
      <DialogActions sx={{ 
        p: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        gap: 1,
        position: isMobile ? 'sticky' : 'static',
        bottom: 0,
        bgcolor: 'background.paper'
      }}>
        <Button 
          onClick={onClose}
          variant="outlined"
          sx={{ flex: isMobile ? 1 : 'none' }}
        >
          Άκυρο
        </Button>
        <Button 
          onClick={onSave} 
          variant="contained" 
          color="success"
          sx={{ 
            flex: isMobile ? 2 : 'none',
            fontWeight: 600,
            py: { xs: 1.5, sm: 1 }
          }}
        >
          {selectedCell?.existingShift ? 'Ενημέρωση' : 'Προσθήκη'} 
          {quickAddForm.selectedDays.length > 1 && ` (${quickAddForm.selectedDays.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

